import { type ReactElement, type ReactNode, useState } from 'react';
import { type FoodFulfillmentPendingDraftView, type FoodFulfillmentPolicyVersionView, type FoodFulfillmentLockedView } from '@vello/api-client';
import { type FormatContext, formatDistance, formatMoney, formatNumber } from '@vello/format';
import { compareLocked, minorFromWire, worstDirection, type LockedDirection, formatMarketInstant, marketZoneOfContext, scopeTimeZone } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, EmptyState, Field, Notice, PageHeader, Panel, TextInput, FormRow, Text, Stack, Row as LayoutRow, Strong, PanelHead, Box, Grid } from '@vello/web-theme';
import { PersonName } from '../components/person-name';
import { LoadingBlock } from '../components/loading-block';
import { DetailRow as Row } from '../components/detail-row';

const DIRECTION_TONE: Readonly<Record<LockedDirection, 'neutral' | 'success' | 'danger' | 'warning'>> = { tightened: 'success', unchanged: 'neutral', loosened: 'danger' };

interface LoadedState {
  readonly policyId: string;
  readonly active: FoodFulfillmentPolicyVersionView | undefined;
  readonly versions: readonly FoodFulfillmentPolicyVersionView[];
  /** Candidates awaiting a SECOND operator's approval (D350) — nothing here is live. */
  readonly pending: readonly FoodFulfillmentPendingDraftView[];
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <Stack as="section" gap={2}>
      <Text as="h3" role="label" tone="secondary">{title}</Text>
      {children}
    </Stack>
  );
}

/**
 * **Política de entrega Food** (fulfillment-config governance, M19/S195) — the platform operator surface over
 * `/v1/food-fulfillment-config/*`. It shows the currently-active governed policy version, the full published
 * version history, and — per version — how its locked legal/safety constraints moved relative to the prior version
 * (the **no-loosening guard**: the server refuses a publish that loosens below the governing baseline). An operator
 * can roll the live pointer back to a prior published version. Server-authoritative (operator AAL2 + `config:*`).
 *
 * FOUR EYES (D350). Publishing is a two-operator act: one submits a candidate version, a SECOND approves it, and
 * the approval is what publishes and activates. This screen is the checker's side of that — it lists the
 * candidates awaiting approval and offers Approve / Reject with a mandatory reason. The server decides who may
 * approve (it refuses a self-approval 403 by comparing the authenticated principal to the recorded author), so
 * this UI never tries to hide the button from the author: it surfaces the refusal instead of pretending to be
 * the gate. Every action's reason is recorded on the governance trail.
 */
export function FoodFulfillmentConfigScreen(): ReactElement {
  const { runtime, scope: operatorScope, markets } = useAdmin();
  const { confirm, toast } = useConsole();
  const { t, lang } = useT();
  const { fail } = useVocab();
  // D401: instants render in the operating market's zone (AGENTS: store UTC, render in the market zone).
  // When the scope names no single zone the render falls back to UTC and SAYS so (`formatMarketInstant`).
  const fmtCtx: FormatContext = { locale: lang, timeZone: scopeTimeZone(operatorScope.current, markets) ?? 'UTC', measurement: 'metric' };

  const [policyId, setPolicyId] = useState('');
  const [loaded, setLoaded] = useState<LoadedState | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [errorId, setErrorId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const trimmed = policyId.trim();

  const load = async (id: string): Promise<void> => {
    if (loading || id.length === 0) return;
    setLoading(true);
    setErrorId(undefined);
    const [activeRes, versionsRes, pendingRes] = await Promise.all([
      runtime.foodFulfillmentConfig.getActive(id),
      runtime.foodFulfillmentConfig.listVersions(id),
      runtime.foodFulfillmentConfig.listPending(id),
    ]);
    if (!versionsRes.ok) {
      setLoaded(undefined);
      setErrorId(t('food.err', { code: fail(versionsRes.error) }));
      setLoading(false);
      return;
    }
    // A policy with history but nothing live answers 404 on `active`; that is a valid state, not an error.
    setLoaded({
      policyId: id,
      active: activeRes.ok ? activeRes.value.policy : undefined,
      versions: versionsRes.value.versions,
      pending: pendingRes.ok ? pendingRes.value.pending : [],
    });
    setLoading(false);
  };

  /** A mandatory free-text reason for a governance action — what lands on the audit trail. */
  const askReason = async (spec: { title: string; body: string; confirmLabel: string; reasonLabel: string; tone: 'primary' | 'danger' }): Promise<string | undefined> => {
    const r = await confirm({
      title: spec.title, body: spec.body, confirmLabel: spec.confirmLabel, cancelLabel: t('ui.cancel'), tone: spec.tone,
      // D358: without `requireNote` the dialog rendered no reason field (it only did for chips), so the mandatory
      // reason could never be typed and approve/reject/rollback silently returned here.
      reasonLabel: spec.reasonLabel, notePlaceholder: t('foodFulfil.reason.note.ph'), requireNote: true,
    });
    if (!r.confirmed) return undefined;
    const reason = (r.note ?? r.reason ?? '').trim();
    return reason.length > 0 ? reason : undefined;
  };

  const rollback = async (toVersion: number): Promise<void> => {
    if (busy || loaded === undefined) return;
    const reason = await askReason({
      title: t('foodFulfil.rollback.confirm.title'), body: t('foodFulfil.rollback.confirm.body', { n: toVersion }),
      confirmLabel: t('foodFulfil.rollback.confirm'), reasonLabel: t('foodFulfil.rollback.reasonLabel'), tone: 'danger',
    });
    if (reason === undefined) return;
    setBusy(true);
    const res = await runtime.foodFulfillmentConfig.rollback(loaded.policyId, toVersion, reason);
    setBusy(false);
    if (res.ok) { toast({ message: t('foodFulfil.rollback.toast', { n: toVersion }), tone: 'success' }); await load(loaded.policyId); }
    else toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  // Step 2 of four-eyes. The server refuses a self-approval with 403; that refusal surfaces as the normal error
  // toast, so the checker sees WHY rather than a silently missing button.
  const approveVersion = async (version: number): Promise<void> => {
    if (busy || loaded === undefined) return;
    const reason = await askReason({
      title: t('foodFulfil.approve.confirm.title', { n: version }), body: t('foodFulfil.approve.confirm.body'),
      confirmLabel: t('foodFulfil.approve.confirm'), reasonLabel: t('foodFulfil.approve.reasonLabel'), tone: 'primary',
    });
    if (reason === undefined) return;
    setBusy(true);
    const res = await runtime.foodFulfillmentConfig.approveVersion(loaded.policyId, version, reason);
    setBusy(false);
    if (res.ok) { toast({ message: t('foodFulfil.approve.toast', { n: version }), tone: 'success' }); await load(loaded.policyId); }
    else toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  const rejectVersion = async (version: number): Promise<void> => {
    if (busy || loaded === undefined) return;
    const reason = await askReason({
      title: t('foodFulfil.reject.confirm.title', { n: version }), body: t('foodFulfil.reject.confirm.body'),
      confirmLabel: t('foodFulfil.reject.confirm'), reasonLabel: t('foodFulfil.reject.reasonLabel'), tone: 'danger',
    });
    if (reason === undefined) return;
    setBusy(true);
    const res = await runtime.foodFulfillmentConfig.rejectVersion(loaded.policyId, version, reason);
    setBusy(false);
    if (res.ok) { toast({ message: t('foodFulfil.reject.toast', { n: version }), tone: 'success' }); await load(loaded.policyId); }
    else toast({ message: t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  return (
    <>
      <PageHeader title={t('foodFulfil.title')} subtitle={t('foodFulfil.subtitle')} />

      <div><Notice tone="neutral">{t('foodFulfil.guard.explain')}</Notice></div>

      <Panel>
        <FormRow
          action={
            <Button variant="secondary" onClick={() => void load(trimmed)} disabled={loading || trimmed.length === 0}>{t('ui.load')}</Button>
          }
        >
          <Field label={t('foodFulfil.field.policyId')} htmlFor="ff-policy" hint={t('foodFulfil.field.policyId.hint')}>
            <TextInput id="ff-policy" value={policyId} onChange={(e) => setPolicyId(e.target.value)} placeholder={t('foodFulfil.field.policyId.ph')} />
          </Field>
        </FormRow>
      </Panel>

      {errorId !== undefined && <div><Notice tone="danger">{errorId}</Notice></div>}

      {loading ? (
        <Panel><LoadingBlock /></Panel>
      ) : loaded === undefined ? (
        <Panel><EmptyState title={t('foodFulfil.prompt')} detail={t('foodFulfil.prompt.detail')} /></Panel>
      ) : (
        <Stack gap={5}>
          <PendingQueue
            pending={loaded.pending}
            fmtCtx={fmtCtx}
            busy={busy}
            onApprove={(v) => void approveVersion(v)}
            onReject={(v) => void rejectVersion(v)}
          />
          {loaded.active !== undefined ? <ActiveVersion version={loaded.active} fmtCtx={fmtCtx} /> : <Notice tone="warning">{t('foodFulfil.noActive')}</Notice>}
          <VersionHistory versions={loaded.versions} activeVersion={loaded.active?.version} fmtCtx={fmtCtx} busy={busy} onRollback={(v) => void rollback(v)} />
        </Stack>
      )}
    </>
  );
}

function LockedBlock({ locked, fmtCtx, currency }: { locked: FoodFulfillmentLockedView; fmtCtx: FormatContext; currency: string }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  return (
    <>
      <Row label={t('foodFulfil.locked.radius')} value={formatDistance(locked.maxDeliveryRadiusMeters, fmtCtx)} />
      <Row label={t('foodFulfil.locked.orderCap')} value={locked.maxOrderValueMinor !== undefined ? formatMoney(minorFromWire(locked.maxOrderValueMinor, currency), fmtCtx) : t('foodFulfil.locked.uncapped')} />
      <Stack gap={1}>
        <Text role="caption" tone="secondary">{t('foodFulfil.locked.prohibited')}</Text>
        <LayoutRow gap={1} align="stretch" wrap>
          {locked.prohibitedItemCategories.length === 0 ? (
            <Text role="caption" tone="tertiary">{t('foodFulfil.locked.none')}</Text>
          ) : (
            locked.prohibitedItemCategories.map((c) => <Badge key={c} tone="neutral">{v('food.prohibited_item_category', c)}</Badge>)
          )}
        </LayoutRow>
      </Stack>
    </>
  );
}

/**
 * The checker's queue (D350): candidates a colleague submitted that are waiting for a second pair of eyes.
 * Nothing listed here is live — approving is what publishes and activates. The author and their reason lead the
 * row, because deciding whether to approve starts with who asked and why.
 */
function PendingQueue({ pending, fmtCtx, busy, onApprove, onReject }: {
  pending: readonly FoodFulfillmentPendingDraftView[];
  fmtCtx: FormatContext;
  busy: boolean;
  onApprove: (v: number) => void;
  onReject: (v: number) => void;
}): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  return (
    <Panel><Stack gap={4}>
      <LayoutRow gap={2} align="center">
        <Text as="h2" role="title" className="vello-grow">{t('foodFulfil.pending.title')}</Text>
        {pending.length > 0 && <Badge tone="warning">{pending.length}</Badge>}
      </LayoutRow>
      <Text as="p" role="caption" tone="secondary">{t('foodFulfil.pending.explain')}</Text>
      {pending.length === 0 ? (
        <EmptyState title={t('foodFulfil.pending.none')} />
      ) : (
        <Stack gap={4}>
          {[...pending].sort((a, b) => a.version - b.version).map((d) => (
            <Box key={d.version} gap={3}>
              <LayoutRow gap={3} align="baseline" wrap>
                <Strong role="body">{t('foodFulfil.version', { n: d.version })}</Strong>
                <Badge tone="warning">{v('food.fulfillment_version_status', d.status)}</Badge>
                <Text role="caption" tone="tertiary" mono className="vello-admin-a-end">{formatMarketInstant(d.submittedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)}</Text>
              </LayoutRow>
              <Row label={t('foodFulfil.field.submittedBy')} mono={false}><PersonName id={d.submittedBy} /></Row>
              <Row label={t('foodFulfil.field.reason')} value={d.reason} />
              <Row label={t('foodFulfil.fees.base')} value={formatMoney(minorFromWire(d.policy.fees.baseFeeMinor, d.policy.fees.currency), fmtCtx)} />
              <Row label={t('foodFulfil.locked.radius')} value={formatDistance(d.policy.locked.maxDeliveryRadiusMeters, fmtCtx)} />
              <LayoutRow gap={2} align="stretch">
                <Button size="sm" onClick={() => onApprove(d.version)} disabled={busy}>{t('foodFulfil.approve.action')}</Button>
                <Button size="sm" variant="secondary" onClick={() => onReject(d.version)} disabled={busy}>{t('foodFulfil.reject.action')}</Button>
              </LayoutRow>
            </Box>
          ))}
        </Stack>
      )}
    </Stack></Panel>
  );
}

function ActiveVersion({ version, fmtCtx }: { version: FoodFulfillmentPolicyVersionView; fmtCtx: FormatContext }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const p = version.policy;
  const money = (minor: string): string => formatMoney(minorFromWire(minor, p.fees.currency), fmtCtx);
  return (
    <Panel><Stack gap={4}>
      <LayoutRow gap={2} align="center">
        <Text as="h2" role="title" className="vello-grow">{t('foodFulfil.version', { n: version.version })}</Text>
        <Badge tone="success">{t('foodFulfil.active')}</Badge>
      </LayoutRow>
      <Grid min="md" gap={5}>
        <Section title={t('foodFulfil.section.provenance')}>
          <Row label={t('foodFulfil.field.status')} value={v('food.fulfillment_version_status', version.status)} />
          {/* Four-eyes maker. Versions published before D350 recorded no author — say so rather than leave a
              blank row that reads as if the approver wrote it. */}
          <Row label={t('foodFulfil.field.submittedBy')} mono={false}><PersonName id={version.submittedBy} fallback={t('foodFulfil.field.author.unknown')} /></Row>
          {version.submittedAt !== undefined && <Row label={t('foodFulfil.field.submittedAt')} value={formatMarketInstant(version.submittedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)} />}
          <Row label={t('foodFulfil.field.approvedBy')} mono={false}><PersonName id={version.approvedBy} /></Row>
          <Row label={t('foodFulfil.field.approvedAt')} value={formatMarketInstant(version.approvedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)} />
          <Row label={t('foodFulfil.field.publishedAt')} value={formatMarketInstant(version.publishedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)} />
          <Row label={t('foodFulfil.field.reason')} value={version.reason} />
        </Section>
        <Section title={t('foodFulfil.section.scope')}>
          <Row label={t('foodFulfil.scope.market')} value={p.scope.marketRef} />
          <Row label={t('foodFulfil.scope.service')} value={p.scope.serviceRef} />
          {p.scope.geofenceRef !== undefined && <Row label={t('foodFulfil.scope.geofence')} value={p.scope.geofenceRef} />}
        </Section>
        <Section title={t('foodFulfil.section.fees')}>
          <Row label={t('foodFulfil.fees.base')} value={money(p.fees.baseFeeMinor)} />
          <Row label={t('foodFulfil.fees.distance')} value={money(p.fees.distanceRateMinorPerThousandMeters)} />
          <Row label={t('foodFulfil.fees.service')} value={t('foodFulfil.fees.bps', { bps: p.fees.serviceFeeBps })} />
        </Section>
        <Section title={t('foodFulfil.section.capacity')}>
          <Row label={t('foodFulfil.capacity.concurrent')} value={formatNumber(p.capacity.maxConcurrentOrders, fmtCtx)} />
          <Row label={t('foodFulfil.capacity.window')} value={t('foodFulfil.capacity.windowValue', { n: p.capacity.maxOrdersPerWindow, s: p.capacity.windowSeconds })} />
          <Row label={t('foodFulfil.batching.title')} value={p.batching.enabled ? t('foodFulfil.batching.on', { size: p.batching.maxBatchSize, detour: formatDistance(p.batching.detourCapMeters, fmtCtx) }) : t('foodFulfil.batching.off')} />
        </Section>
        <Section title={t('foodFulfil.section.locked')}>
          <LockedBlock locked={p.locked} fmtCtx={fmtCtx} currency={p.fees.currency} />
        </Section>
        <Section title={t('foodFulfil.section.zones')}>
          {p.zones.length === 0 ? (
            <Text role="caption" tone="tertiary">{t('foodFulfil.zones.none')}</Text>
          ) : (
            p.zones.map((z) => <Row key={z.zoneId} label={z.zoneId} value={`${formatDistance(z.minMeters, fmtCtx)} – ${formatDistance(z.maxMeters, fmtCtx)}`} />)
          )}
        </Section>
      </Grid>
    </Stack></Panel>
  );
}

function VersionHistory({ versions, activeVersion, fmtCtx, busy, onRollback }: { versions: readonly FoodFulfillmentPolicyVersionView[]; activeVersion: number | undefined; fmtCtx: FormatContext; busy: boolean; onRollback: (v: number) => void }): ReactElement {
  const { t } = useT();
  const rows = [...versions].sort((a, b) => a.version - b.version);
  return (
    <Panel padded={false}>
      <PanelHead title={t('foodFulfil.history.title')} actions={<Badge tone="neutral">{String(rows.length)}</Badge>} />
      {rows.length === 0 ? (
        <EmptyState title={t('foodFulfil.history.empty')} />
      ) : (
        rows.map((v, i) => {
          const prior = i > 0 ? rows[i - 1] : undefined;
          const cmp = prior !== undefined ? compareLocked(prior.policy.locked, v.policy.locked) : undefined;
          const dir = cmp !== undefined ? worstDirection(cmp.rows.map((r) => r.direction)) : undefined;
          const isActive = v.version === activeVersion;
          return (
            <Box key={v.version} gap={2} tone="ruled">
              <LayoutRow gap={3} align="center" wrap>
                <Strong role="body">{t('foodFulfil.version', { n: v.version })}</Strong>
                {isActive && <Badge tone="success">{t('foodFulfil.active')}</Badge>}
                {dir !== undefined ? <Badge tone={DIRECTION_TONE[dir]}>{t(`foodFulfil.dir.${dir}`)}</Badge> : <Badge tone="neutral">{t('foodFulfil.dir.initial')}</Badge>}
                <Text role="caption" tone="tertiary" mono className="vello-admin-a-end">{formatMarketInstant(v.publishedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)}</Text>
              </LayoutRow>
              {cmp?.loosens === true && <Notice tone="warning">{t('foodFulfil.dir.loosenedNote')}</Notice>}
              <Text as="div" role="caption" tone="secondary"><LayoutRow gap={4} align="stretch" wrap>
                <span>{t('foodFulfil.locked.radius')}: {formatDistance(v.policy.locked.maxDeliveryRadiusMeters, fmtCtx)}</span>
                <span>{t('foodFulfil.locked.prohibited')}: {String(v.policy.locked.prohibitedItemCategories.length)}</span>
                <span>{t('foodFulfil.field.submittedBy')}: <PersonName id={v.submittedBy} fallback={t('foodFulfil.field.author.unknown')} /></span>
                <span>{t('foodFulfil.field.approvedBy')}: <PersonName id={v.approvedBy} /></span>
              </LayoutRow></Text>
              {!isActive && <div><Button size="sm" variant="secondary" onClick={() => onRollback(v.version)} disabled={busy}>{t('foodFulfil.rollback.action')}</Button></div>}
            </Box>
          );
        })
      )}
    </Panel>
  );
}
